import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, ChevronDown, Coffee, Dumbbell, Palette, Mountain, MapPin,
  Users, Sparkles, Instagram, Linkedin, Youtube, Check, ShieldCheck,
  EyeOff, Flag, Handshake, Copy, Send, Gift, Radio, Plus,
} from 'lucide-react';
import api from '../../lib/api';
import './marketing.css';

// ── Motion helpers ────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

function Reveal({ children, delay = 0, className = '', as = 'div' }) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className}>{children}</div>;
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
      transition={{ delay }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

// ── Nav ────────────────────────────────────────────────────────────
function Nav({ onCTA }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  return (
    <header
      data-testid="marketing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/70 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
        <a href="#top" data-testid="marketing-logo" className="text-white font-black text-2xl tracking-tighter">
          asap<span className="text-[#B6E600]">.</span>
        </a>
        <nav className="hidden md:flex items-center gap-9 text-[13px] font-semibold text-white/75">
          <a href="#how" className="hover:text-white transition">How it works</a>
          <a href="#app" className="hover:text-white transition">The app</a>
          <a href="#safety" className="hover:text-white transition">Safety</a>
          <a href="#faq" className="hover:text-white transition">FAQ</a>
        </nav>
        <button
          onClick={onCTA}
          data-testid="nav-cta"
          className="bg-[#B6E600] text-[#0B0B0B] font-bold text-[13px] px-5 py-2.5 rounded-full hover:brightness-110 transition-all"
        >
          Join waitlist
        </button>
      </div>
    </header>
  );
}

// ── Hero (cinematic cross-fade montage) ───────────────────────────
// Six curated lifestyle stills cross-fading with Ken Burns motion — plays
// like a Nike/Apple commercial without needing shot footage. Swap any URL
// in HERO_STILLS for real ASAP moments the moment you have them.
const HERO_STILLS = [
  { src: '/hero/hero1.png', alt: 'Dubai skyline with Burj Khalifa at golden hour' },
  { src: '/hero/hero2.png', alt: 'Friends laughing on a Dubai Marina rooftop at sunset' },
  { src: '/hero/hero3.png', alt: 'Emirati desert dunes at golden hour' },
  { src: '/hero/hero4.png', alt: 'Modern majlis gathering with Arabic coffee' },
  { src: '/hero/hero5.png', alt: 'Dubai Marina waterfront at blue hour' },
  { src: '/hero/hero6.png', alt: 'Gold Souk in Old Dubai at dusk' },
];
const HERO_FRAME_MS = 4200;

function Hero({ onCTA, onSecondary }) {
  const ref = useRef(null);
  const [frame, setFrame] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(
      () => setFrame(f => (f + 1) % HERO_STILLS.length),
      HERO_FRAME_MS,
    );
    return () => clearInterval(id);
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100dvh] flex items-end pt-28 pb-16 md:pb-24 overflow-hidden marketing-noise"
    >
      <motion.div aria-hidden style={reduced ? {} : { y }} className="absolute inset-0 z-0">
        {/* Cross-fade montage */}
        <div data-testid="hero-montage" className="absolute inset-0">
          {HERO_STILLS.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={s.alt}
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`marketing-kenburns absolute inset-0 w-full h-[110%] object-cover transition-opacity duration-[1400ms] ease-in-out ${
                i === frame ? 'opacity-100' : 'opacity-0'
              }`}
              data-active={i === frame}
              data-testid={`hero-slide-${i}`}
            />
          ))}
        </div>
        <div className="absolute inset-0 marketing-hero-vignette" />
      </motion.div>

      <motion.div style={reduced ? {} : { opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <Reveal>
          <span className="overline">Early access &middot; UAE</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="display text-[52px] sm:text-[80px] md:text-[112px] lg:text-[136px] mt-5 max-w-5xl">
            Life happens<br />
            <span className="text-[#B6E600]">in real life.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={onCTA}
              data-testid="hero-primary-cta"
              className="group bg-[#B6E600] text-[#0B0B0B] font-black text-[14px] tracking-tight px-7 py-4 rounded-full hover:brightness-110 transition-all inline-flex items-center gap-2"
            >
              Get the app
              <ArrowRight size={16} strokeWidth={3} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={onSecondary}
              data-testid="hero-secondary-cta"
              className="text-white font-bold text-[14px] px-7 py-4 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur transition"
            >
              How it works
            </button>
          </div>
        </Reveal>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 marketing-scroll-hint text-white/50 text-[11px] uppercase tracking-[0.3em] font-bold flex flex-col items-center gap-1">
        Scroll <ChevronDown size={14} />
      </div>
    </section>
  );
}

// ── Live Activity ticker ──────────────────────────────────────────
// Rotating strip that communicates ASAP is a live product built around
// real-world moments. Pre-launch these are illustrative examples of the
// kinds of activity the app will surface; they can be swapped for live
// backend data (`/api/discovery/pulse` or similar) once we have volume.
const LIVE_ACTIVITY_EXAMPLES = [
  '12 people just joined a beach volleyball game in Dubai',
  'A coffee meetup starts in 18 minutes in Alserkal',
  '3 new plans were created in Abu Dhabi today',
  'A padel group in Al Wasl is looking for one more player',
  'A sunset run at Kite Beach fills up in 4 minutes',
  'A photography walk in DIFC has 6 spots left',
  'A weekend hike in Hatta just went live',
  'A creative workshop in D3 is open for the evening',
];

function LiveActivity() {
  // Double the list so the marquee loops seamlessly at 50% translation
  const items = [...LIVE_ACTIVITY_EXAMPLES, ...LIVE_ACTIVITY_EXAMPLES];
  return (
    <div
      data-testid="live-activity-strip"
      className="bg-[#0F0F0F] border-y border-white/5 py-3 marketing-ticker"
    >
      <div className="marketing-ticker__track">
        {items.map((msg, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 text-[13px] text-white/70 font-medium tracking-tight"
          >
            <Radio size={12} className="text-[#B6E600]" strokeWidth={2.6} />
            <span className="text-white/90">{msg}</span>
            <span aria-hidden className="text-white/20 ml-2">•</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  const [open, setOpen] = useState(0);
  const steps = [
    {
      n: '01', t: 'Discover',
      short: 'Plans near you, matched to what you love.',
      long: 'Find plans nearby based on your interests, schedule and location. No infinite feeds — every card is something you could actually show up to.',
      features: ['Nearby plans', 'Interest matching', 'AI recommendations', 'Distance', 'Friends attending'],
    },
    {
      n: '02', t: 'Join',
      short: 'One tap. Hosts approve on compatibility.',
      long: 'Request a spot instantly. Hosts approve based on shared interests and reputation, not follower counts.',
      features: ['One-tap request', 'Host approval', 'Compatibility match', 'Reputation signals', 'Instant confirm'],
    },
    {
      n: '03', t: 'Meet',
      short: 'Show up. Put the phone away.',
      long: 'The app fades away the moment the plan starts. What happens next is the reason ASAP exists.',
      features: ['Directions', 'Check-in', 'Live attendee list', 'Group chat', 'Focus mode'],
    },
    {
      n: '04', t: 'Belong',
      short: 'One night becomes an ongoing crew.',
      long: 'Turn any great meetup into a lasting community with its own chat, recurring plans, and shared memories.',
      features: ['Communities', 'Recurring plans', 'Shared memories', 'Group chats', 'Roles & hosts'],
    },
  ];
  return (
    <section id="how" data-testid="how-it-works-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal><span className="overline">How it works</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="display text-4xl sm:text-6xl md:text-7xl mt-5 max-w-3xl">
            Discover. Join. <span className="text-white/40">Meet.</span>{' '}
            <span className="text-[#B6E600]">Belong.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          {steps.map((s, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={s.n} delay={i * 0.06}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`how-step-${s.n}`}
                  aria-expanded={isOpen}
                  className={`w-full text-left rounded-3xl border p-6 md:p-7 transition-all group ${
                    isOpen
                      ? 'bg-[#141414] border-[#B6E600]/40'
                      : 'bg-[#141414] border-white/5 hover:border-white/15'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className={`display text-4xl transition-colors ${isOpen ? 'text-[#B6E600]' : 'text-[#B6E600]/40 group-hover:text-[#B6E600]'}`}>
                        {s.n}
                      </p>
                      <h3 className="mt-4 text-2xl font-black text-white tracking-tight">{s.t}</h3>
                    </div>
                    <span
                      className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-transform ${
                        isOpen
                          ? 'bg-[#B6E600] text-[#0B0B0B] border-[#B6E600] rotate-45'
                          : 'border-white/20 text-white/70'
                      }`}
                      aria-hidden
                    >
                      <Plus size={14} strokeWidth={3} />
                    </span>
                  </div>
                  <AnimatePresence initial={false} mode="wait">
                    {isOpen ? (
                      <motion.div
                        key="long"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        data-testid={`how-step-long-${s.n}`}
                        className="overflow-hidden"
                      >
                        <p className="mt-4 text-white/70 text-[15px] leading-relaxed">{s.long}</p>
                        <div
                          data-testid={`how-step-features-${s.n}`}
                          className="mt-5 flex flex-wrap gap-2"
                        >
                          {s.features.map((f, fi) => (
                            <motion.span
                              key={f}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.35, delay: 0.1 + fi * 0.05 }}
                              className="text-[11px] font-bold uppercase tracking-wide bg-[#B6E600]/10 text-[#B6E600] border border-[#B6E600]/25 px-2.5 py-1 rounded-full"
                            >
                              {f}
                            </motion.span>
                          ))}
                        </div>
                      </motion.div>
                    ) : (
                      <p className="mt-4 text-white/60 text-[14px] leading-relaxed">
                        {s.short}
                      </p>
                    )}
                  </AnimatePresence>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── The App — merged product tour + core promises + community pills
function TheApp() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y1 = useTransform(scrollYProgress, [0, 1], ['0%', '-16%']);
  const y2 = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const y3 = useTransform(scrollYProgress, [0, 1], ['0%', '-24%']);
  const reduced = useReducedMotion();

  const shots = [
    { src: '/marketing/screen-home.jpg',    label: 'Home' },
    { src: '/marketing/screen-explore.jpg', label: 'Explore' },
    { src: '/marketing/screen-profile.jpg', label: 'Profile' },
  ];

  const promises = [
    'No infinite feeds. No dark patterns.',
    'A profile designed to earn trust in seconds.',
    'Recurring plans grow into their own communities.',
  ];

  const communities = ['Coffee', 'Fitness', 'Hikes', 'Networking', 'Art', 'Wellness'];

  return (
    <section
      ref={ref}
      id="app"
      data-testid="the-app-section"
      className="py-24 md:py-36 bg-[#0F0F0F] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal><span className="overline">The app</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="display text-4xl sm:text-6xl md:text-7xl mt-5">
              Your city,<br />in your pocket.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-9 space-y-3">
              {promises.map(item => (
                <li key={item} className="flex items-start gap-3 text-white/85 text-[15px] leading-snug">
                  <span className="mt-1 w-4 h-4 rounded-full bg-[#B6E600]/20 flex items-center justify-center shrink-0">
                    <Check size={10} className="text-[#B6E600]" strokeWidth={3.5} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <p className="text-[11px] uppercase tracking-[0.24em] text-white/40 font-bold mb-3">
                Communities you can create
              </p>
              <div className="flex flex-wrap gap-2" data-testid="community-pills">
                {communities.map(c => (
                  <span key={c} className="marketing-chip !cursor-default">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="relative h-[520px] md:h-[600px]">
          {shots.map((s, i) => {
            const ys = [y1, y2, y3][i];
            const style = reduced ? {} : { y: ys };
            const pos = [
              'left-0 top-6 w-[46%] rotate-[-6deg]',
              'right-0 top-0 w-[46%] rotate-[4deg]',
              'left-[26%] bottom-0 w-[48%] rotate-[-2deg] z-10',
            ][i];
            return (
              <motion.div
                key={s.label}
                style={style}
                className={`absolute ${pos} marketing-phone`}
                data-testid={`product-shot-${s.label.toLowerCase()}`}
              >
                <img src={s.src} alt={`ASAP ${s.label} screen`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur-md rounded-2xl px-3 py-2 text-[10px] font-bold text-white/90 uppercase tracking-widest">
                  {s.label}
                </div>
              </motion.div>
            );
          })}

          {/* Floating annotation chips — lightweight "Apple tour" feel */}
          {!reduced && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                data-testid="annot-match"
                className="absolute top-4 -left-2 z-20 bg-black/85 backdrop-blur-xl border border-[#B6E600]/40 rounded-2xl px-3.5 py-2 shadow-2xl"
              >
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#B6E600] font-black">Compatibility</p>
                <p className="text-lg font-black text-white leading-tight tracking-tight">92% match</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid="annot-going"
                className="absolute top-32 -right-4 z-20 bg-[#B6E600] rounded-full px-4 py-2 shadow-2xl"
              >
                <p className="text-[13px] font-black text-[#0B0B0B] tracking-tight">12 going</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid="annot-plan"
                className="absolute bottom-8 -left-4 z-20 bg-black/85 backdrop-blur-xl border border-white/10 rounded-2xl px-3.5 py-2 shadow-2xl"
              >
                <p className="text-[9px] uppercase tracking-[0.2em] text-white/50 font-black">Tonight · Fitness</p>
                <p className="text-sm font-black text-white leading-tight tracking-tight">Sunset run · Kite Beach</p>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Safety ────────────────────────────────────────────────────────
function Safety() {
  const items = [
    { icon: ShieldCheck, t: 'Verified emails' },
    { icon: Handshake,   t: 'Host-approved joins' },
    { icon: Flag,        t: 'Reporting & blocking' },
    { icon: EyeOff,      t: 'Private accounts' },
  ];
  return (
    <section id="safety" data-testid="safety-section" className="py-24 md:py-36">
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <Reveal><span className="overline">Safety</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="display text-4xl sm:text-6xl md:text-7xl mt-5">
            Trust is designed in.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-white/60 max-w-xl mx-auto text-[15px] leading-relaxed">
            The basics that make real-life meetups possible — built on day one.
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.05}>
              <div className="rounded-2xl bg-[#141414] border border-white/5 p-5 h-full">
                <div className="w-9 h-9 mx-auto rounded-full bg-[#B6E600]/15 flex items-center justify-center">
                  <it.icon size={16} className="text-[#B6E600]" strokeWidth={2.4} />
                </div>
                <p className="mt-4 text-sm font-black text-white leading-tight tracking-tight">{it.t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Waitlist form ─────────────────────────────────────────────────
const CITY_OPTIONS = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Al Ain', 'Ajman', 'Fujairah', 'Ras Al Khaimah', 'Umm Al Quwain', 'Other'];
const INTEREST_OPTIONS = [
  { key: 'fitness', label: 'Fitness', icon: Dumbbell },
  { key: 'coffee', label: 'Coffee', icon: Coffee },
  { key: 'networking', label: 'Networking', icon: Users },
  { key: 'food', label: 'Food', icon: Sparkles },
  { key: 'outdoors', label: 'Outdoors', icon: Mountain },
  { key: 'art', label: 'Art', icon: Palette },
  { key: 'wellness', label: 'Wellness', icon: Sparkles },
  { key: 'social', label: 'Social', icon: Users },
  { key: 'sports', label: 'Sports', icon: Dumbbell },
  { key: 'travel', label: 'Travel', icon: MapPin },
];

function Waitlist({ formRef, onSuccess }) {
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [interests, setInterests] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [inviter, setInviter] = useState(null);
  const [myCode, setMyCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlRef = (params.get('ref') || '').trim().toUpperCase();
    let storedRef = '';
    try {
      const raw = localStorage.getItem('asap_ref');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.code && parsed.expires_at && parsed.expires_at > Date.now()) {
          storedRef = parsed.code;
        }
      }
    } catch { /* ignore */ }
    if (urlRef) {
      try {
        localStorage.setItem('asap_ref', JSON.stringify({
          code: urlRef,
          expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000,
        }));
      } catch { /* ignore */ }
    }
    const activeRef = urlRef || storedRef;
    if (activeRef) {
      api.get(`/waitlist/ref/${activeRef}`)
        .then(r => setInviter(r.data))
        .catch(() => setInviter(null));
    }
  }, []);

  const toggle = (k) => setInterests(prev => (
    prev.includes(k) ? prev.filter(x => x !== k) : (prev.length >= 6 ? prev : [...prev, k])
  ));

  const readActiveRef = () => {
    try {
      const raw = localStorage.getItem('asap_ref');
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      if (parsed.code && parsed.expires_at && parsed.expires_at > Date.now()) return parsed.code;
    } catch { /* ignore */ }
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading'); setError('');
    if (!email || !city) { setStatus('err'); setError('Email and city are required.'); return; }
    try {
      const { data } = await api.post('/waitlist', {
        email, city, interests, ref: readActiveRef(),
      });
      setStatus(data.status === 'already_on_list' ? 'already' : 'ok');
      setMyCode(data.referral_code || '');
      try { localStorage.removeItem('asap_ref'); } catch { /* ignore */ }
      onSuccess?.(data);
    } catch (err) {
      setStatus('err');
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  const myReferralUrl = myCode ? `${window.location.origin}/welcome?ref=${myCode}` : '';

  const copyLink = async () => {
    if (!myReferralUrl) return;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(myReferralUrl);
        ok = true;
      }
    } catch { /* blocked */ }
    if (!ok) {
      try {
        const el = document.createElement('textarea');
        el.value = myReferralUrl;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        ok = true;
      } catch { /* give up */ }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const shareLink = async () => {
    if (!myReferralUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on ASAP',
          text: 'ASAP is a new app for spontaneous plans and communities in the UAE. Join the waitlist:',
          url: myReferralUrl,
        });
      } catch { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  // No small numbers. Confident language only, until traction is impressive.
  const socialProof = 'Opening access city by city.';

  return (
    <section id="waitlist" data-testid="waitlist-form-section" ref={formRef} className="py-24 md:py-36 relative">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Reveal><span className="overline text-center block">You in?</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="display text-4xl sm:text-6xl md:text-7xl mt-5 text-center">
            Join the waitlist.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-white/60 text-center max-w-md mx-auto text-[15px] leading-relaxed">
            Launching across the UAE. {socialProof} Be among the first members.
          </p>
        </Reveal>

        {inviter && status !== 'ok' && status !== 'already' && (
          <Reveal delay={0.15}>
            <div
              data-testid="inviter-banner"
              className="mt-10 mx-auto max-w-xl bg-[#B6E600]/10 border border-[#B6E600]/30 rounded-2xl px-5 py-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-[#B6E600]/20 flex items-center justify-center shrink-0">
                <Gift size={17} className="text-[#B6E600]" strokeWidth={2.4} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#B6E600] font-bold">Invited by</p>
                <p className="text-white text-sm font-bold mt-0.5 truncate">
                  {inviter.masked_email}
                  {inviter.city && <span className="text-white/50 font-medium"> · {inviter.city}</span>}
                </p>
              </div>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.2}>
          <form
            onSubmit={submit}
            data-testid="waitlist-form"
            className="mt-12 rounded-[32px] bg-[#141414] border border-white/10 p-6 md:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#B6E600]/10 blur-3xl pointer-events-none" />
            <div className="relative">
              {status === 'ok' || status === 'already' ? (
                <div data-testid="waitlist-success" className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#B6E600] flex items-center justify-center mb-6">
                    <Check size={30} className="text-[#0B0B0B]" strokeWidth={3.5} />
                  </div>
                  <h3 className="display text-3xl text-white">
                    {status === 'already' ? "You're already on the list." : "You're in."}
                  </h3>
                  <p className="mt-3 text-white/60 max-w-sm mx-auto">
                    We&apos;ll email <span className="text-white font-bold">{email}</span> when
                    ASAP opens in {city}.
                  </p>

                  {myReferralUrl && (
                    <div data-testid="referral-card" className="mt-10 text-left bg-black/40 border border-[#B6E600]/25 rounded-2xl p-5 md:p-6">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-[#B6E600] font-bold">Move up the queue</p>
                      <p className="mt-2 text-white font-bold text-lg">Every friend who joins moves you up.</p>
                      <div
                        data-testid="referral-url"
                        className="mt-4 bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 font-mono text-[12.5px] text-[#B6E600] break-all"
                      >
                        {myReferralUrl}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={copyLink}
                          data-testid="copy-referral"
                          className="bg-white/10 hover:bg-white/15 text-white font-bold text-[13px] px-4 py-2.5 rounded-full flex items-center gap-2 transition"
                        >
                          <Copy size={14} strokeWidth={2.5} />
                          {copied ? 'Copied!' : 'Copy link'}
                        </button>
                        <button
                          type="button"
                          onClick={shareLink}
                          data-testid="share-referral"
                          className="bg-[#B6E600] hover:brightness-110 text-[#0B0B0B] font-black text-[13px] px-4 py-2.5 rounded-full flex items-center gap-2 transition"
                        >
                          <Send size={14} strokeWidth={2.8} />
                          Share
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <label className="block text-[11px] uppercase tracking-[0.24em] text-white/40 font-bold mb-2">Email</label>
                  <input
                    data-testid="waitlist-email"
                    type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@domain.com"
                    className="w-full bg-black/40 text-white rounded-2xl px-5 py-4 text-[15px] border border-white/10 focus:border-[#B6E600] focus:outline-none placeholder-white/30 transition"
                  />

                  <label className="block text-[11px] uppercase tracking-[0.24em] text-white/40 font-bold mb-2 mt-6">Your city</label>
                  <div className="flex flex-wrap gap-2">
                    {CITY_OPTIONS.map(c => (
                      <button
                        type="button" key={c}
                        onClick={() => setCity(c)}
                        data-testid={`waitlist-city-${c.replace(/\s+/g, '-').toLowerCase()}`}
                        data-active={city === c}
                        className="marketing-chip"
                      >
                        {c}
                      </button>
                    ))}
                  </div>

                  <label className="block text-[11px] uppercase tracking-[0.24em] text-white/40 font-bold mb-2 mt-6">
                    What are you into?{' '}
                    <span className="text-white/30 normal-case tracking-normal">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.map(({ key, label, icon: Icon }) => (
                      <button
                        type="button" key={key}
                        onClick={() => toggle(key)}
                        data-testid={`waitlist-interest-${key}`}
                        data-active={interests.includes(key)}
                        className="marketing-chip"
                      >
                        <Icon size={12} strokeWidth={2.6} />
                        {label}
                      </button>
                    ))}
                  </div>

                  {error && (
                    <p data-testid="waitlist-error" className="mt-4 text-[13px] text-rose-400">{error}</p>
                  )}

                  <button
                    type="submit"
                    data-testid="waitlist-submit"
                    disabled={status === 'loading'}
                    className="mt-8 w-full bg-[#B6E600] text-[#0B0B0B] font-black tracking-tight py-4 rounded-full hover:brightness-110 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    {status === 'loading' ? 'Adding you\u2026' : 'Join the waitlist'}
                    {status !== 'loading' && <ArrowRight size={16} strokeWidth={3.2} />}
                  </button>
                  <p className="mt-4 text-center text-white/40 text-[11px]">
                    By joining, you agree to receive occasional emails about ASAP.
                  </p>
                </>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

// ── FAQ (4 essential questions) ───────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(-1);
  const items = [
    { q: 'Is ASAP available in my city?', a: 'ASAP is live across the UAE. We\u2019re opening city by city — waitlist members get priority access as each city goes live.' },
    { q: 'Is it a dating app?',       a: 'No. ASAP is for platonic meetups — friendships, hobbies, communities.' },
    { q: 'What does it cost?',        a: 'The core app is free. Optional premium features may launch later, but discovering, joining, and meeting will always be free.' },
    { q: 'How is it kept safe?',      a: 'Verified emails, host-approved joins, private-account controls, and a dedicated safety team.' },
  ];
  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-36 bg-[#0F0F0F]">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Reveal><span className="overline">Questions</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="display text-4xl sm:text-6xl md:text-7xl mt-5">Ask us.</h2>
        </Reveal>
        <div className="mt-12 divide-y divide-white/10 border-t border-b border-white/10">
          {items.map((it, i) => (
            <div key={it.q} data-testid={`faq-item-${i}`} className="py-5">
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 text-left group"
              >
                <span className="text-white font-bold text-lg md:text-xl">{it.q}</span>
                <span className={`w-8 h-8 rounded-full border border-white/15 flex items-center justify-center shrink-0 transition-transform ${open === i ? 'rotate-45 bg-[#B6E600] text-[#0B0B0B] border-[#B6E600]' : 'text-white/70'}`}>
                  <span className="text-lg leading-none">+</span>
                </span>
              </button>
              {open === i && (
                <p className="mt-4 text-white/70 text-[15px] leading-relaxed max-w-2xl">{it.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Footer ─────────────────────────────────────────────────────────
function Footer({ onCTA }) {
  return (
    <footer data-testid="footer-section" className="pt-24 md:pt-32 pb-10 relative overflow-hidden border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <Reveal><span className="overline">Ready?</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="display text-5xl sm:text-7xl md:text-8xl mt-5 leading-none">
                See you<br />on the list.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <button
                onClick={onCTA}
                data-testid="footer-cta"
                className="mt-10 bg-[#B6E600] text-[#0B0B0B] font-black text-[14px] px-7 py-4 rounded-full hover:brightness-110 transition inline-flex items-center gap-2"
              >
                Join the waitlist <ArrowRight size={16} strokeWidth={3} />
              </button>
            </Reveal>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-6 md:mt-4">
            <FooterCol title="Product" links={[
              { l: 'How it works', h: '#how' },
              { l: 'The app', h: '#app' },
              { l: 'Safety', h: '#safety' },
              { l: 'FAQ', h: '#faq' },
            ]} />
            <FooterCol title="Legal" links={[
              { l: 'Terms', h: '/legal/terms' },
              { l: 'Privacy', h: '/legal/privacy' },
              { l: 'Community', h: '/legal/community' },
              { l: 'Contact', h: 'mailto:hello@asaphello.com' },
            ]} />
          </div>
        </div>

        <div className="mt-20 flex flex-wrap items-center justify-between gap-6 text-white/40 text-[12px] border-t border-white/10 pt-6">
          <p>© 2026 ASAP · Dubai · UAE</p>
          <div className="flex items-center gap-4">
            <a href="https://instagram.com" className="hover:text-white transition" aria-label="Instagram"><Instagram size={16} /></a>
            <a href="https://linkedin.com" className="hover:text-white transition" aria-label="LinkedIn"><Linkedin size={16} /></a>
            <a href="https://youtube.com" className="hover:text-white transition" aria-label="YouTube"><Youtube size={16} /></a>
          </div>
        </div>

        <div className="mt-14 pointer-events-none select-none overflow-hidden">
          <p className="display text-white/[0.04] text-[24vw] leading-[0.85] tracking-tighter">asap.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="overline !text-white/40 mb-4">{title}</p>
      <ul className="space-y-2.5">
        {links.map(({ l, h }) => (
          <li key={l}>
            <a href={h} className="text-white/70 hover:text-white text-sm transition">{l}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Root marketing page ────────────────────────────────────────────
export default function Marketing() {
  const formRef = useRef(null);
  const scrollToWaitlist = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToHow = () => {
    document.getElementById('how')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="marketing-root">
      <Nav onCTA={scrollToWaitlist} />
      <Hero onCTA={scrollToWaitlist} onSecondary={scrollToHow} />
      <LiveActivity />
      <HowItWorks />
      <TheApp />
      <Safety />
      <Waitlist formRef={formRef} />
      <FAQ />
      <Footer onCTA={scrollToWaitlist} />
    </div>
  );
}
